/*eslint import-x/namespace: ['error', { allowComputed: true }]*/
import type { AxiosRequestConfig } from 'axios';

import { v4 } from 'uuid';

import { DOCUMENT_UPLOAD_CONCURRENCY } from '~/common/constants/files';
import { intlService } from '~/i18n/IntlService';
import * as apiServices from '~/services/serviceInstances';
import { bytesToBlob } from '~/utils/files';
import { mapWithConcurrencySettled } from '~/utils/mapWithConcurrency';

const { pilot } = apiServices;

type ApiServiceName = keyof typeof apiServices;

export interface UploadFileApiConfig {
  service: ApiServiceName;
  method?: 'post' | 'put' | 'patch';
  url: string;
  payload?: FormData;
  config?: AxiosRequestConfig;
}

export interface UploadDocumentFile extends File {
  folderId?: string | number;
  upload: { uuid: string };
  upload_form_data?: unknown;
  uploadCb?: unknown;
}

export interface UploadDocumentOptions {
  apiConfig: UploadFileApiConfig;
  onUploadStart: (file: UploadDocumentFile) => void;
  onUploadProgress: (
    file: UploadDocumentFile,
    event: { total?: number; loaded: number },
  ) => void;
  onUploadError: (file: UploadDocumentFile, error: unknown) => void;
  onUploadComplete: (file: UploadDocumentFile, resp: unknown) => void;
}

export async function uploadAttachment(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return await pilot.post('/attachment/', formData);
}

// TODO: Switch these and uploadAtttachment(s) in api/tickets.js to not accept callbacks here
export const uploadAttachments = <T>(
  files: File[],
  callback: (data: T[]) => void,
  errorHandler: (promise: Promise<unknown>) => void,
) => {
  const promises = files.map((file) => uploadAttachment(file));
  const promisesResolved = promises.map((promise) =>
    promise.catch(() => {
      errorHandler(promise);
    }),
  );
  return pilot
    .concurrentRequests<T>(promisesResolved)
    .then((data) => callback(data));
};

export async function downloadAttachment(attachmentId: string | number) {
  const config = { responseType: 'blob' as const };

  let original;
  try {
    original = await pilot.get(`/attachment/${attachmentId}`, config);
  } catch {
    original = null;
  }

  return bytesToBlob(original, original?.type);
}

export async function downloadDocumentAttachment(
  documentHandlerId: string | number,
  attachmentId: string | number,
) {
  const config = { responseType: 'blob' as const };

  let original;
  try {
    original = await pilot.get(
      `document/${documentHandlerId}/attachment/${attachmentId}`,
      config,
    );
  } catch {
    original = null;
  }

  return bytesToBlob(original, original?.type);
}

export function uploadFile({
  service,
  method = 'post',
  url,
  payload,
  config,
}: UploadFileApiConfig) {
  return apiServices[service][method](url, payload, config);
}

export const uploadNewVersion = async ({
  documentId,
  file,
}: {
  documentId: string | number;
  file: File;
}) => {
  try {
    const payload = new FormData();
    payload.append('id', v4());
    payload.append('file', file);
    payload.append('filename', file.name);
    payload.append('size_in_bytes', String(file.size));

    return await pilot.post(`/version/document/${documentId}/`, payload);
  } catch {
    const intl = intlService.getIntl();
    throw new Error(
      intl.formatMessage({
        id: 'Document.uploadNewVersionError',
        defaultMessage: 'Failed to upload new version',
        description: 'Error message when document version upload fails',
      }),
    );
  }
};

export function getFileUploadRequestPayload(file: UploadDocumentFile) {
  const payload = new FormData();
  const form = {
    id: file.upload.uuid,
    file,
    folder: file.folderId,
    filename: file.name,
    upload_form_data: JSON.stringify(file.upload_form_data),
    size_in_bytes: file.size,
  };

  for (const [k, v] of Object.entries(form)) {
    payload.set(k, v as string | Blob);
  }

  return payload;
}

export async function uploadDocument(
  file: UploadDocumentFile,
  {
    apiConfig,
    onUploadStart,
    onUploadProgress,
    onUploadError,
    onUploadComplete,
  }: UploadDocumentOptions,
) {
  const payload = getFileUploadRequestPayload(file);

  const config = {
    onUploadProgress: (event: { total?: number; loaded: number }) =>
      onUploadProgress(file, event),
    // The service-wide 2 minute timeout counts from when the request is sent,
    // not from when the browser grants it a connection, so a large file (or one
    // waiting behind the pool) aborts before it ever reaches the server.
    timeout: 0,
  };

  onUploadStart(file);

  let resp;

  try {
    resp = await uploadFile({
      ...apiConfig,
      payload,
      config,
    });
    onUploadComplete(file, resp);
  } catch (error) {
    onUploadError(file, error);
  }

  return resp;
}

export const uploadDocuments = async (
  files: UploadDocumentFile[],
  options: UploadDocumentOptions,
) => {
  // Settled rather than fail-fast: a callback that throws for one file must not
  // strand the files still queued behind it.
  const settled = await mapWithConcurrencySettled(
    files,
    DOCUMENT_UPLOAD_CONCURRENCY,
    (file) => uploadDocument(file, options),
  );

  return settled.map((result) =>
    result.status === 'fulfilled' ? result.value : undefined,
  );
};
